import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Loader2,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { SUMMIT, RSVP_COHORTS } from "@/constants/summit";
import { MaskedLine, Reveal } from "@/components/Reveal";

const API = "https://www.thechoicesummit.online/api";

const EMPTY = {
  full_name: "",
  title: "",
  organization: "",
  email: "",
  phone: "",
  cohort: "",
  concierge: "",
};

const ConfirmAttendance = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [pass, setPass] = useState(null);

  /**
   * Generic input handler
   */
  const set = (key) => (event) => {
    const value = event.target.value;

    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  /**
   * Submit RSVP
   */
  const submit = async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    if (!form.cohort) {
      toast.error("Please select your audience cohort.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await axios.post(`${API}/rsvp`, form);

      // Store the submitted form together with API response.
      setPass({
        ...data,
        ...form,
      });

      toast.success("Attendance confirmed. Your VVIP pass is ready.");
    } catch (error) {
      const detail = error?.response?.data?.detail;

      toast.error(
        typeof detail === "string"
          ? detail
          : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reset the form and return to RSVP form.
   */
  const submitAnother = () => {
    setPass(null);
    setForm({ ...EMPTY });
  };

  return (
    <main data-testid="confirm-attendance-page" className="pt-[72px]">
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,87,34,0.12),transparent_55%)]" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-5">

            {/* =========================================================
                LEFT CONTENT
            ========================================================= */}
            <div className="lg:col-span-2">
              <div className="mb-6 flex items-center gap-4">
                <span className="h-px w-10 bg-brand-500" />

                <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-300">
                  VVIP Access
                </span>
              </div>

              <h1 className="font-display text-4xl font-black leading-[1.0] tracking-tight sm:text-5xl lg:text-6xl">
                <MaskedLine delay={0.1}>
                  Confirm your
                </MaskedLine>

                <MaskedLine delay={0.24}>
                  <span className="text-brand-500">
                    attendance.
                  </span>
                </MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.45,
                }}
                className="mt-6 text-base leading-relaxed text-white/60"
              >
                This summit is strictly for invited guests. Submit your
                details and our concierge will confirm your VVIP pass by
                email.
              </motion.p>

              <Reveal
                delay={0.2}
                className="mt-10 space-y-4"
              >
                <div className="flex items-center gap-3 text-sm text-white/65">
                  <CalendarDays className="h-4 w-4 text-brand-400" />

                  {SUMMIT.dateLabel} · {SUMMIT.timeLabel}
                </div>

                <div className="flex items-center gap-3 text-sm text-white/65">
                  <MapPin className="h-4 w-4 text-brand-400" />

                  {SUMMIT.venue}, {SUMMIT.address}, {SUMMIT.city}
                </div>

                <div className="flex items-center gap-3 text-sm text-white/65">
                  <ShieldCheck className="h-4 w-4 text-brand-400" />

                  Invitees only · details reviewed by the host concierge
                </div>
              </Reveal>
            </div>

            {/* =========================================================
                RIGHT CONTENT
                IMPORTANT:
                No AnimatePresence is used here.
                This prevents React/Framer Motion/Radix Portal DOM
                removal conflicts.
            ========================================================= */}
            <div className="lg:col-span-3">
              {pass ? (
                <motion.div
                  key="success-pass"
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  data-testid="rsvp-success-pass-card"
                  className="orange-glow relative overflow-hidden rounded-2xl border border-brand-500/50 bg-charcoal p-8 sm:p-12"
                >
                  {/* Top orange line */}
                  <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-brand-600 via-brand-400 to-brand-600" />

                  {/* Success icon */}
                  <span className="grid h-14 w-14 place-items-center rounded-full border border-brand-500/40 bg-brand-500/15">
                    <BadgeCheck className="h-7 w-7 text-brand-400" />
                  </span>

                  <h2 className="mt-6 font-display text-3xl font-black">
                    You're confirmed.
                  </h2>

                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    Thank you, {pass.full_name}. Your attendance at{" "}
                    {SUMMIT.name} — {SUMMIT.tagline} is confirmed
                    {pass.email_notified
                      ? " and a confirmation email is on its way to you"
                      : ""}
                    . Present this pass ID at the VVIP concierge desk.
                  </p>

                  {/* Pass information */}
                  <div className="mt-8 grid gap-4 rounded-xl border border-white/10 bg-obsidian p-6 sm:grid-cols-2">

                    {/* Pass ID */}
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        VVIP Pass ID
                      </p>

                      <p
                        data-testid="rsvp-pass-id"
                        className="mt-1 font-mono text-2xl font-bold text-brand-400"
                      >
                        {pass.pass_id}
                      </p>
                    </div>

                    {/* Cohort */}
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        Cohort
                      </p>

                      <p className="mt-1 font-display text-lg font-semibold">
                        {pass.cohort}
                      </p>
                    </div>

                    {/* Date */}
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        Date
                      </p>

                      <p className="mt-1 text-sm text-white/75">
                        {SUMMIT.dateLabel}
                      </p>
                    </div>

                    {/* Venue */}
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        Venue
                      </p>

                      <p className="mt-1 text-sm text-white/75">
                        {SUMMIT.venue}, {SUMMIT.address}, {SUMMIT.city}
                      </p>
                    </div>
                  </div>

                  {/* Submit another attendee */}
                  <button
                    type="button"
                    data-testid="rsvp-submit-another-button"
                    onClick={submitAnother}
                    className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-brand-300 transition-colors hover:text-brand-400"
                  >
                    Submit another attendee →
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="rsvp-form"
                  onSubmit={submit}
                  initial={{
                    opacity: 0,
                    y: 30,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  data-testid="rsvp-form"
                  className="rounded-2xl border border-white/10 bg-charcoal/70 p-6 backdrop-blur-sm sm:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">

                    {/* =====================================================
                        FULL NAME
                    ===================================================== */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="full_name"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Full Name *
                      </Label>

                      <Input
                        id="full_name"
                        data-testid="rsvp-form-full-name-input"
                        required
                        minLength={2}
                        value={form.full_name}
                        onChange={set("full_name")}
                        placeholder="Dr. Jane Mokoena"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>

                    {/* =====================================================
                        TITLE / ROLE
                    ===================================================== */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="title"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Title / Role *
                      </Label>

                      <Input
                        id="title"
                        data-testid="rsvp-form-title-input"
                        required
                        minLength={2}
                        value={form.title}
                        onChange={set("title")}
                        placeholder="Chief Executive Officer"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>

                    {/* =====================================================
                        ORGANIZATION
                    ===================================================== */}
                    <div className="space-y-2 sm:col-span-2">
                      <Label
                        htmlFor="organization"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Organization *
                      </Label>

                      <Input
                        id="organization"
                        data-testid="rsvp-form-organization-input"
                        required
                        minLength={2}
                        value={form.organization}
                        onChange={set("organization")}
                        placeholder="Company, practice or agency"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>

                    {/* =====================================================
                        EMAIL
                    ===================================================== */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Email *
                      </Label>

                      <Input
                        id="email"
                        type="email"
                        data-testid="rsvp-form-email-input"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>

                    {/* =====================================================
                        PHONE
                    ===================================================== */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Mobile *
                      </Label>

                      <Input
                        id="phone"
                        type="tel"
                        data-testid="rsvp-form-phone-input"
                        required
                        minLength={5}
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+27 82 000 0000"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>

                    {/* =====================================================
                        COHORT
                    ===================================================== */}
                    <div className="space-y-2 sm:col-span-2">
                      <Label
                        htmlFor="cohort"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        I am attending as *
                      </Label>

                      <Select
                        value={form.cohort}
                        onValueChange={(value) => {
                          setForm((current) => ({
                            ...current,
                            cohort: value,
                          }));
                        }}
                      >
                        <SelectTrigger
                          id="cohort"
                          data-testid="rsvp-form-cohort-select"
                          className="border-white/15 bg-obsidian text-white focus:ring-brand-500"
                        >
                          <SelectValue placeholder="Select your cohort" />
                        </SelectTrigger>

                        <SelectContent className="border-white/15 bg-charcoal text-white">
                          {RSVP_COHORTS.map((cohort) => (
                            <SelectItem
                              key={cohort}
                              value={cohort}
                              data-testid={`rsvp-cohort-option-${cohort
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                            >
                              {cohort}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* =====================================================
                        CONCIERGE REQUEST
                    ===================================================== */}
                    <div className="space-y-2 sm:col-span-2">
                      <Label
                        htmlFor="concierge"
                        className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60"
                      >
                        Concierge Requests{" "}
                        <span className="text-white/30">
                          (optional — dietary, access, assistant)
                        </span>
                      </Label>

                      <Textarea
                        id="concierge"
                        data-testid="rsvp-form-concierge-textarea"
                        rows={3}
                        value={form.concierge}
                        onChange={set("concierge")}
                        placeholder="Anything our concierge should prepare for you?"
                        className="border-white/15 bg-obsidian text-white placeholder:text-white/25 focus-visible:ring-brand-500"
                      />
                    </div>
                  </div>

                  {/* =====================================================
                      SUBMIT BUTTON
                  ===================================================== */}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="rsvp-form-submit-button"
                    className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-8 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:bg-brand-600 hover:shadow-[0_0_40px_rgba(255,87,34,0.55)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Confirming…
                      </>
                    ) : (
                      <>
                        Confirm My Attendance
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  {/* =====================================================
                      FOOTER NOTE
                  ===================================================== */}
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                    Your details are sent directly to the host concierge at{" "}
                    {SUMMIT.host}.
                  </p>
                </motion.form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ConfirmAttendance;
